// Detail page for a single blåtur: group photo, hosts, participants, description and image gallery

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getFirestore, doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { FiArrowLeft, FiChevronLeft, FiChevronRight, FiX, FiMapPin, FiEdit2 } from 'react-icons/fi';
import { app } from './firebase-config';
import { useAuthUser, tripYear, tripTitle, tripCover } from './blaturData';
import { useRoles } from './adminData';
import { SmartImage } from './imageUtils';

const db = getFirestore(app);

function BlaturTur() {
  const { tripId } = useParams();
  const user = useAuthUser();
  const { isBlaturAdmin } = useRoles(user);
  const [trip, setTrip] = useState(undefined);
  const [userMap, setUserMap] = useState({});
  const [viewerIndex, setViewerIndex] = useState(null);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const [tripSnap, usersSnap] = await Promise.all([
          getDoc(doc(db, 'events', tripId)),
          getDocs(collection(db, 'users')),
        ]);
        setTrip(tripSnap.exists() ? { id: tripSnap.id, ...tripSnap.data() } : null);
        const map = {};
        usersSnap.docs.forEach(d => {
          const name = d.data().name;
          if (name) map[name.toLowerCase()] = d.id;
        });
        setUserMap(map);
      } catch (error) {
        console.error('Error fetching trip: ', error);
        setTrip(null);
      }
    };
    load();
  }, [user, tripId]);

  const images = trip?.images || [];
  const closeViewer = useCallback(() => setViewerIndex(null), []);
  const step = useCallback((delta) => {
    setViewerIndex(i => (i === null ? i : (i + delta + images.length) % images.length));
  }, [images.length]);

  useEffect(() => {
    if (viewerIndex === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeViewer();
      else if (e.key === 'ArrowLeft') step(-1);
      else if (e.key === 'ArrowRight') step(1);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [viewerIndex, closeViewer, step]);

  const renderName = (name) => {
    const userId = userMap[name.toLowerCase()];
    return userId
      ? <Link key={name} to={`/profil/${userId}`} className="blatur-chip">{name}</Link>
      : <span key={name} className="blatur-chip">{name}</span>;
  };

  if (user === null) return <div className="blatur-page"><div className="blatur-empty">Logg inn for å se turen.</div></div>;
  if (user === undefined || trip === undefined) return <div className="blatur-page"><div className="blatur-empty">Laster…</div></div>;
  if (trip === null) {
    return (
      <div className="blatur-page">
        <Link to="/blatur" className="blatur-back"><FiArrowLeft /> Alle blåturer</Link>
        <div className="blatur-empty">Fant ikke denne turen.</div>
      </div>
    );
  }

  const cover = tripCover(trip);
  const participants = trip.Deltagelse || [];

  return (
    <div className="blatur-page">
      <div className="blatur-detail-nav">
        <Link to="/blatur" className="blatur-back"><FiArrowLeft /> Alle blåturer</Link>
        {isBlaturAdmin && <Link to={`/admin?event=${trip.id}`} className="blatur-back"><FiEdit2 /> Rediger</Link>}
      </div>

      <div className="blatur-hero">
        {cover ? <SmartImage src={cover} size={1600} alt={`Gruppebilde ${tripTitle(trip)}`} loading="eager" /> : <div className="blatur-card-placeholder"><FiMapPin /></div>}
      </div>

      <div className="blatur-detail-head">
        <span className="blatur-detail-year">{tripYear(trip)}</span>
        <h1>{tripTitle(trip)}</h1>
      </div>

      <div className="blatur-detail-grid">
        <section className="blatur-section">
          <h2>Arrangører</h2>
          <div className="blatur-chips">{(trip.Vert || []).map(renderName)}</div>
          <h2>Deltakere ({participants.length})</h2>
          <div className="blatur-chips">{participants.map(renderName)}</div>
        </section>

        {trip.description?.trim() && (
          <section className="blatur-section">
            <h2>Om turen</h2>
            <p className="blatur-description">{trip.description}</p>
          </section>
        )}
      </div>

      <section className="blatur-section">
        <div className="blatur-gallery-head">
          <h2>Bilder ({images.length})</h2>
          <Link to="/bilder" className="blatur-gallery-link">Last opp under Bilder</Link>
        </div>
        {images.length > 0 ? (
          <div className="blatur-gallery">
            {images.map((url, index) => (
              <button key={url} className="blatur-thumb" onClick={() => setViewerIndex(index)}>
                <SmartImage src={url} size={800} />
              </button>
            ))}
          </div>
        ) : (
          <p className="blatur-muted">Ingen bilder fra denne turen ennå.</p>
        )}
      </section>

      {viewerIndex !== null && images[viewerIndex] && (
        <div className="blatur-viewer" onClick={closeViewer}>
          <button className="blatur-viewer-close" onClick={closeViewer} aria-label="Lukk"><FiX /></button>
          {images.length > 1 && (
            <button className="blatur-viewer-nav prev" onClick={(e) => { e.stopPropagation(); step(-1); }} aria-label="Forrige"><FiChevronLeft /></button>
          )}
          <SmartImage src={images[viewerIndex]} size={1600} loading="eager" onClick={(e) => e.stopPropagation()} />
          {images.length > 1 && (
            <button className="blatur-viewer-nav next" onClick={(e) => { e.stopPropagation(); step(1); }} aria-label="Neste"><FiChevronRight /></button>
          )}
          <span className="blatur-viewer-count">{viewerIndex + 1} / {images.length}</span>
        </div>
      )}
    </div>
  );
}

export default BlaturTur;
